/**
 * CG-7 — the guardian PRE-LINK state: rendered on /guardian when the
 * signed-in guardian account has no ACTIVE athletes yet (fresh from the
 * onboarding fork, or every invite still pending). Invite form + the
 * open outbound invites ("waiting for them to join").
 *
 * All data flows through useRelationships (the authorized API). The
 * invite confirmation copy stays GENERIC (anti-enumeration — never
 * reveal whether the email has an account); with CG-7 the invite works
 * either way, which the copy now says plainly.
 */

import { useState } from 'react';
import type { FormEvent } from 'react';
import { useRelationships } from '../../hooks/useRelationships';
import styles from './GuardianInvitePanel.module.css';

export const INVITE_RECORDED_COPY =
  'Request recorded. They’ll see it when they sign in, even if they create their account later.';

export function GuardianInvitePanel() {
  const { enabled, relationships, loading, invite, revoke } = useRelationships();
  const [email, setEmail] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  // Server-driven visibility, same contract as GuardiansSection.
  if (loading && relationships.length === 0) return null;
  if (!enabled) return null;

  const outbound = relationships.filter(
    (r) => r.mySide === 'adult' && r.role === 'guardian' && r.status === 'invited',
  );

  const onInvite = async (e: FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;
    setBusy('invite');
    setError(null);
    setNotice(null);
    try {
      await invite(trimmed);
      setEmail('');
      setNotice(INVITE_RECORDED_COPY);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  };

  const onWithdraw = async (id: string) => {
    setBusy(id);
    setError(null);
    try {
      await revoke(id);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className={styles.panel} aria-labelledby="guardian-invite-title">
      <h2 id="guardian-invite-title" className={styles.heading}>
        Invite your athlete
      </h2>
      <p className={styles.lede}>
        Enter the email they use, or will use, to sign in. If they
        don&rsquo;t have an account yet, the invite connects the moment they
        sign up. Nothing is shared until they accept.
      </p>

      <form className={styles.form} onSubmit={(e) => void onInvite(e)}>
        <div className={styles.row}>
          <input
            type="email"
            className={styles.input}
            placeholder="athlete@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            aria-label="Athlete email"
          />
          <button
            type="submit"
            className={styles.sendBtn}
            disabled={busy === 'invite' || !email.trim()}
          >
            Send invite
          </button>
        </div>
        {notice && <p className={styles.notice}>{notice}</p>}
      </form>

      {outbound.length > 0 && (
        <ul className={styles.pendingList} role="list">
          {outbound.map((rel) => (
            <li key={rel.id} className={styles.pendingItem}>
              <div className={styles.pendingWho}>
                <span className={styles.pendingEmail}>
                  {rel.counterpart.name || rel.counterpart.email || 'Pending invite'}
                </span>
                <span className={styles.pendingStatus}>
                  Waiting for them to accept
                </span>
              </div>
              <button
                type="button"
                className={styles.withdrawBtn}
                disabled={busy === rel.id}
                onClick={() => void onWithdraw(rel.id)}
              >
                Withdraw
              </button>
            </li>
          ))}
        </ul>
      )}

      {error && (
        <p className={styles.errorNote} role="alert">
          {error}
        </p>
      )}
    </section>
  );
}
